/**
 * audio.ts — Procedural VFX using Web Audio API
 */

// Global AudioContext (lazy initialized to comply with browser autoplay policies)
let audioCtx: AudioContext | null = null;
let isAudioEnabled = true;

/**
 * Initializes the AudioContext if it hasn't been initialized yet.
 * Must be called as a result of a user gesture.
 */
function initAudio() {
  if (!isAudioEnabled) return;
  try {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }
  } catch (e) {
    console.warn('Web Audio API not supported or blocked', e);
    isAudioEnabled = false;
  }
}

/**
 * Plays a retro "pew" shooting sound.
 */
export function playShootSound(): void {
  initAudio();
  if (!audioCtx || !isAudioEnabled) return;

  const t = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  // "Pew" shape: start high pitch and quickly slide down
  osc.type = 'square';
  osc.frequency.setValueAtTime(800, t);
  osc.frequency.exponentialRampToValueAtTime(100, t + 0.15);

  // Volume envelope: sharp attack, quick decay
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.15, t + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);

  osc.connect(gain);
  gain.connect(audioCtx.destination);

  osc.start(t);
  osc.stop(t + 0.15);
}

/**
 * Plays a retro noise burst explosion sound.
 */
export function playExplosionSound(): void {
  initAudio();
  if (!audioCtx || !isAudioEnabled) return;

  const t = audioCtx.currentTime;
  
  // Create a buffer with white noise
  const bufferSize = Math.floor(audioCtx.sampleRate * 0.5); // 0.5 seconds of noise
  const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  const noise = audioCtx.createBufferSource();
  noise.buffer = buffer;

  // Use a low-pass filter to make the noise sound more like a deep explosion
  const filter = audioCtx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1000, t);
  filter.frequency.linearRampToValueAtTime(100, t + 0.4);

  const gain = audioCtx.createGain();
  // Volume envelope: instant loud, slower decay
  gain.gain.setValueAtTime(1.0, t); // Louder initial volume
  gain.gain.linearRampToValueAtTime(0.01, t + 0.5);

  noise.connect(filter);
  filter.connect(gain);
  gain.connect(audioCtx.destination);

  noise.start(t);
}

/**
 * Plays a descending multi-tone "death" sound.
 */
export function playDeathSound(): void {
  initAudio();
  if (!audioCtx || !isAudioEnabled) return;

  const t = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  osc.type = 'sawtooth';
  
  // Start mid, step down in arpeggio style
  osc.frequency.setValueAtTime(300, t);
  osc.frequency.exponentialRampToValueAtTime(150, t + 0.1);
  osc.frequency.setValueAtTime(150, t + 0.1);
  osc.frequency.exponentialRampToValueAtTime(75, t + 0.25);
  osc.frequency.setValueAtTime(75, t + 0.25);
  osc.frequency.exponentialRampToValueAtTime(30, t + 0.5);

  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.2, t + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);

  osc.connect(gain);
  gain.connect(audioCtx.destination);

  osc.start(t);
  osc.stop(t + 0.5);
}

/**
 * Plays a short, sharp ticking sound for the timer.
 */
export function playTickSound(): void {
  initAudio();
  if (!audioCtx || !isAudioEnabled) return;

  const t = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();

  // A very short, sharp click/tick
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(800, t); // static pitch, no sweep

  // Volume envelope: instant on, very quick fade
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.5, t + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.02);

  osc.connect(gain);
  gain.connect(audioCtx.destination);

  osc.start(t);
  osc.stop(t + 0.025);
}

/**
 * Plays a triumphant 3-note victory sound.
 */
export function playVictorySound(): void {
  initAudio();
  if (!audioCtx || !isAudioEnabled) return;

  const t = audioCtx.currentTime;
  
  const playNote = (freq: number, startOffset: number, duration: number) => {
    if (!audioCtx) return;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();

    osc.type = 'square';
    osc.frequency.value = freq;

    gain.gain.setValueAtTime(0, t + startOffset);
    gain.gain.linearRampToValueAtTime(0.15, t + startOffset + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, t + startOffset + duration);

    osc.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start(t + startOffset);
    osc.stop(t + startOffset + duration);
  };

  // C4, E4, G4, C5 (Arpeggio up)
  playNote(261.63, 0.0, 0.2); // C4
  playNote(329.63, 0.15, 0.2); // E4
  playNote(392.00, 0.3, 0.2); // G4
  playNote(523.25, 0.45, 0.6); // C5 (Longer)
}

/**
 * Plays a sad descending defeat sound.
 */
export function playDefeatSound(): void {
  initAudio();
  if (!audioCtx || !isAudioEnabled) return;

  const t = audioCtx.currentTime;
  
  const playNote = (freq: number, startOffset: number, duration: number) => {
    if (!audioCtx) return;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();

    osc.type = 'triangle';
    osc.frequency.value = freq;

    gain.gain.setValueAtTime(0, t + startOffset);
    gain.gain.linearRampToValueAtTime(0.2, t + startOffset + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, t + startOffset + duration);

    osc.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start(t + startOffset);
    osc.stop(t + startOffset + duration);
  };

  // Eb4, D4, Db4, C4 (Chromatic down)
  playNote(311.13, 0.0, 0.3); // Eb4
  playNote(293.66, 0.3, 0.3); // D4
  playNote(277.18, 0.6, 0.3); // Db4
  playNote(261.63, 0.9, 0.8); // C4 (Longer)
}
